import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Breadcrumbs from "../../components/pageProps/Breadcrumbs";
import { resetCart } from "../../redux/orebiSlice";
import { emptyCart } from "../../assets/images/index";
import ItemCard from "./ItemCard";
import { makeRequest } from "../../makeRequest";
import { useTranslation } from "react-i18next";
import io from "socket.io-client";

const socket = io("https://back.kayanmarketeg.com");

const Cart = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const products = useSelector((state) => state.orebiReducer.products);
  const [totalAmt, setTotalAmt] = useState("");
  const [shippingCharge, setShippingCharge] = useState("");
  const navigate = useNavigate();

  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    phoneNumber: "",
    area: "",
    address: ""
  });

  const [errors, setErrors] = useState({
    phoneNumber: ""
  });

  const [deliveryAreas, setDeliveryAreas] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Phone number validation function
  const validatePhoneNumber = (phoneNumber) => {
    // Check if it contains only numbers
    if (!/^\d+$/.test(phoneNumber)) {
      return t("cart.phoneNumberMustBeNumeric");
    }
    
    // Check if it's 11 digits long
    if (phoneNumber.length !== 11) {
      return t("cart.phoneNumberMustBe11Digits");
    }
    
    // Check if it starts with a valid prefix (010, 011, 012, or 015)
    if (!['010', '011', '012', '015'].includes(phoneNumber.substring(0, 3))) {
      return t("cart.phoneNumberMustStartWith");
    }
    
    return ""; // No error
  };

  useEffect(() => {
    const fetchDeliveryAreas = async () => {
      try {
        const response = await makeRequest.get("/user/delivery-areas");
        console.log(response);
        if (response.data.status === "1") {
          setDeliveryAreas(response.data.payload);
        }
      } catch (error) {
        console.error("Error fetching delivery areas:", error);
      }
    };

    fetchDeliveryAreas();
  }, []);

  // Cleanup socket connection on component unmount
  useEffect(() => {
    return () => {
      socket.off('orderReceived');
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'area') {
      const selectedArea = deliveryAreas.find(area => area.id === parseInt(value));
      if (selectedArea) {
        setShippingCharge(selectedArea.fixedPrice);
      }
    }

    if (name === 'phoneNumber') {
      // Only update if the input is empty or contains only digits
      if (value === '' || /^\d+$/.test(value)) {
        setCustomerInfo((prev) => ({
          ...prev,
          [name]: value
        }));

        // Clear error when field is emptied or validate on input
        const error = value ? validatePhoneNumber(value) : "";
        setErrors(prev => ({
          ...prev,
          phoneNumber: error
        }));
      }
    } else {
      setCustomerInfo((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCheckout = async () => {
    // Validate phone number before checkout
    const phoneError = validatePhoneNumber(customerInfo.phoneNumber);
    if (phoneError) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: phoneError
      }));
      alert(phoneError);
      return;
    }

    if (
      !customerInfo.name ||
      !customerInfo.phoneNumber ||
      !customerInfo.area ||
      !customerInfo.address
    ) {
      alert(t("cart.fillAllInfo"));
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const selectedArea = deliveryAreas.find(
        (area) => area.id === parseInt(customerInfo.area)
      );
      if (!selectedArea) {
        throw new Error(t("cart.invalidArea"));
      }
      console.log("products");
      console.log(products);
      const orderData = {
        address: customerInfo.address,
        payment_type: "1", // Cash on delivery
        pay_id: `PAY-${Date.now()}`, // Generate a unique payment ID
        items: products.map((item) => ({
          productId: item.id,
          productId: item._id,
          quantity: item.quantity
        })),
        delivery_price: selectedArea.fixedPrice,
        phone_number: customerInfo.phoneNumber, // Remove non-numeric characters
        customer_name: customerInfo.name.trim(),
        delivery_area: selectedArea.name
      };

      // Validate items
      if (!orderData.items.length) {
        throw new Error(t("cart.noItems"));
      }

      const response = await makeRequest.post(
        "/app/guest_order_place",
        orderData
      );

      if (response.data.status === "1") {
        // Emit the new order event to socket server
        socket.emit("newOrder", {
          id: response.data.orderId,
          items: products.map(item => ({
            name: item.name,
            quantity: item.quantity
          })),
          customer: customerInfo.name,
          address: customerInfo.address,
          total: totalAmt + shippingCharge
        });

        dispatch(resetCart());
        navigate("/order-success", {
          state: {
            orderDetails: {
              orderId: response.data.orderId,
              customerName: customerInfo.name,
              phoneNumber: customerInfo.phoneNumber,
              address: customerInfo.address,
              deliveryArea: selectedArea.name,
              totalAmount: totalAmt + shippingCharge,

              items: products.map((item) => ({
                productId: item.id,
                name: item.name,
                quantity: item.quantity,
                price: item.price
              }))
            }
          }
        });
      } else {
        throw new Error(response.data.message || t("cart.orderError"));
      }
    } catch (error) {
      console.error("Checkout error:", error);
      alert(error.message || t("cart.orderError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let price = 0;
    products.map((item) => {
      price += item.price * item.quantity;
      return price;
    });
    setTotalAmt(price);
  }, [products]);



  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-container mx-auto px-4 py-8">
        <Breadcrumbs title={t("cart.cart")} />
        {products.length > 0 ? (
          <div className="pb-20">
            {/* Cart Header */}
            <div className="w-full bg-white shadow-sm hidden mdl:flex items-center px-6 py-4 text-gray-600 font-medium rounded-t-lg border-b">
              <div className="w-2/5">{t("cart.product")}</div>
              <div className="w-3/5 flex justify-end gap-8 pr-8">
                <div>{t("cart.price")}</div>
                <div>{t("cart.quantity")}</div>
                <div>{t("cart.subTotal")}</div>
                <div>{t("cart.remove")}</div>
              </div>
            </div>

            {/* Cart Items */}
            <div className="bg-white shadow-sm rounded-b-lg divide-y divide-gray-100">
              {products.map((item) => (
                <ItemCard key={item._id} item={item} />
              ))}
            </div>

            {/* Reset Cart Button */}
            <button
              onClick={() => dispatch(resetCart())}
              className="mt-6 py-2 px-6 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors duration-200"
            >
              {t("cart.resetCart")}
            </button>

            {/* Checkout Section */}
            <div className="mt-8 grid lg:grid-cols-3 gap-8">
              {/* Order Summary Card */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-semibold mb-4">{t("cart.orderSummary")}</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">{t("cart.subTotal")}</span>
                      <span className="font-medium">EGP {totalAmt}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">{t("cart.shippingCharge")}</span>
                      <span className="font-medium">EGP {shippingCharge}</span>
                    </div>
                    <div className="flex justify-between py-2 text-lg font-semibold">
                      <span>{t("cart.total")}</span>
                      <span>EGP {totalAmt + shippingCharge}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Info Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-semibold mb-6">{t("cart.customerInfo")}</h2>
                  <div className="grid gap-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{t("cart.name")}</label>
                        <input
                          type="text"
                          name="name"
                          value={customerInfo.name}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{t("cart.phoneNumber")}</label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={customerInfo.phoneNumber}
                          onChange={handleInputChange}
                          className={`w-full p-3 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                          placeholder="01XXXXXXXXX"
                        />
                        {errors.phoneNumber && (
                          <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">{t("cart.phoneNumberHint")}</p>
                      </div>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{t("cart.area")}</label>
                        <select
                          name="area"
                          value={customerInfo.area}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        >
                          <option value="">{t("cart.selectArea")}</option>
                          {deliveryAreas.map((area) => (
                            <option key={area.id} value={area.id}>
                              {area.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{t("cart.address")}</label>
                        <textarea
                          name="address"
                          value={customerInfo.address}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all h-24"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <button
                    onClick={handleCheckout}
                    disabled={isSubmitting}
                    className={`
                      w-full mt-6 py-4 px-6 rounded-lg
                      bg-primeColor text-white font-semibold
                      hover:bg-black transition-colors duration-200
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primeColor
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t("cart.processing")}
                      </span>
                    ) : (
                      t("cart.placeOrder")
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col items-center justify-center py-16 bg-white rounded-lg shadow-sm"
          >
            <img
              className="w-64 h-64 object-contain mb-8 opacity-80"
              src={emptyCart}
              alt="Empty Cart"
            />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {t("cart.emptyCart")}
            </h1>
            <p className="text-gray-600 text-center max-w-md mb-8">
              {t("cart.emptyCartMessage")}
            </p>
            <Link to="/shop">
              <button className="bg-primeColor px-8 py-3 rounded-lg text-white font-semibold hover:bg-black transform transition-all duration-200 hover:scale-105 active:scale-100">
                {t("cart.continueShopping")}
              </button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Cart;
